import './ReportNotLoaded.scss';

// Enough rows to run past the bottom of the canvas at any realistic window height, so the table
// reads as clipped rather than as a short list that happens to stop. Deliberately a fixed count
// rather than one derived from the viewport — the rows are furniture, not data.
//
// Each row is 2.5rem, so this covers roughly 1600px of canvas; 22 rows only reached ~880px and
// stopped mid-screen on a tall display, which is the exact impression the clipping avoids. They are
// empty divs, so overshooting costs nothing.
const SKELETON_ROW_COUNT = 40;

// Matches the report table: one wide name column and four narrow figure columns.
const SKELETON_COLUMN_COUNT = 5;

const skeletonColumns = Array.from({ length: SKELETON_COLUMN_COUNT }, (_, index) => index);
const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => index);

interface IProps {
  // Whether the run ended in a failure rather than a cancellation the user asked for. Only affects
  // how the message is announced: a failure is assertive, a cancellation is not.
  hasError?: boolean;
}

// The canvas when no report is on screen: a cancelled run, or one that failed in a way running it
// again could fix. Rendered where selectCanRegenerateReport is true and a retry is worth offering.
//
// There is no button here by design. Running is owned entirely by the Report Settings panel, so the
// canvas only ever says what state the report is in — see PA-3289. The greyed skeleton table stands
// in for the results that would occupy this space.
//
// Those bars are plain divs rather than react-loading-skeleton, which the app uses elsewhere. That
// library earns its keep through a shimmer animation and content-derived sizing; here the bars are
// still by design, every size is fixed by the design, and the whole block is hidden from
// assistive tech — leaving only its markup to work around (a live region and a <br> per bar, the
// latter adding a line box to every row).
const ReportNotLoaded = ({ hasError = false }: IProps) => (
  <div className="ReportNotLoaded">
    {/* Decorative: it depicts the absent table rather than reporting anything, and a screen reader
        walking row after empty row would learn nothing. */}
    <div aria-hidden="true" className="ReportNotLoaded_Skeleton">
      <div className="ReportNotLoaded_SkeletonRow ReportNotLoaded_SkeletonRow--header">
        {skeletonColumns.map((column) => (
          <div className="ReportNotLoaded_SkeletonBar" key={column} />
        ))}
      </div>

      {skeletonRows.map((row) => (
        <div className="ReportNotLoaded_SkeletonRow" key={row}>
          {skeletonColumns.map((column) => (
            <div className="ReportNotLoaded_SkeletonBar" key={column} />
          ))}
        </div>
      ))}
    </div>

    <div className="ReportNotLoaded_Card" role={hasError ? 'alert' : 'status'}>
      <p className="ReportNotLoaded_Title">Report not loaded</p>

      <p className="ReportNotLoaded_Body">
        Confirm your Report Settings, and then select <strong>Run</strong> to load results here.
      </p>
    </div>
  </div>
);

export default ReportNotLoaded;
